/**
 * #5841 — the producer-result-reference contract.
 *
 * A governed operation's output already carries authority (numeric bindings,
 * evidence refs, formula receipts). Composing two operations in one manager
 * plan today has no way to hand a value from the first to the second without
 * the model retyping it, which reopens exactly the fabrication surface the
 * governed envelope exists to close. This file is the shared, module-agnostic
 * contract both sides declare against:
 *
 * - a PRODUCER states which of its own already-published bindings are safe to
 *   pass onward, as `GovernedArgumentExportV1` rows on its envelope;
 * - a CONSUMER states what it is willing to accept, as an optional
 *   `CapabilityTrustedReferenceContractV1` on its manifest requirement.
 *
 * Neither side carries a second copy of the number. An export POINTS at a
 * binding that already exists on the SAME envelope; the manager-owned
 * projector (`governed-argument-export-projector.ts`) is the only code that
 * turns a compatible (export, requirement) pair into a server-minted
 * `ManagerTrustedReference`.
 */

/** How the exported figure is stated — never inferred from its label. */
export const GOVERNED_VALUE_BASES = [
  'run_rate',
  'in_period_p_and_l',
  'cumulative_cash',
  'one_time',
  'rate',
  'reference',
] as const;
export type GovernedValueBasis = (typeof GOVERNED_VALUE_BASES)[number];

export function isGovernedValueBasis(value: unknown): value is GovernedValueBasis {
  return typeof value === 'string' && GOVERNED_VALUE_BASES.some((basis) => basis === value);
}

/** The closed evidence-basis vocabulary an export or a reference may carry. */
export const GOVERNED_EVIDENCE_BASES = ['observed', 'modeled', 'unverified'] as const;
export type GovernedEvidenceBasis = (typeof GOVERNED_EVIDENCE_BASES)[number];

export function isGovernedEvidenceBasis(value: unknown): value is GovernedEvidenceBasis {
  return typeof value === 'string' && GOVERNED_EVIDENCE_BASES.some((basis) => basis === value);
}

/** The closed governance-status vocabulary an export or a reference may carry. */
export const GOVERNED_GOVERNANCE_STATUSES = ['draft', 'approved', 'superseded'] as const;
export type GovernedGovernanceStatus = (typeof GOVERNED_GOVERNANCE_STATUSES)[number];

export function isGovernedGovernanceStatus(value: unknown): value is GovernedGovernanceStatus {
  return (
    typeof value === 'string' && GOVERNED_GOVERNANCE_STATUSES.some((status) => status === value)
  );
}

/**
 * An opaque, module-agnostic currency pointer. Deliberately NOT the M&A
 * `CurrencyRef` in `ma-deal-refs.ts` — this contract is a systemic manager
 * capability, not an M&A-owned one, so it declares its own ref shape rather
 * than importing a domain module's branded type.
 */
export type GovernedCurrencyRef = Readonly<{ refKind: 'governed_currency'; id: string }>;

/** An opaque, module-agnostic period pointer. See `GovernedCurrencyRef` note. */
export type GovernedPeriodRef = Readonly<{ refKind: 'governed_period'; id: string }>;

/**
 * An opaque, module-agnostic scope pointer (deal, commitment, business unit,
 * cost pool — whatever scope class the producer's envelope already declares).
 * The projector never inspects `label`; it exists for a reader-facing
 * receipt, not for compatibility matching (matching uses `scopeKind`/`scopeId`).
 */
export type GovernedScopeRef = Readonly<{
  scopeKind: string;
  scopeId: string;
  label?: string;
}>;

/** The closed reference vocabulary that may cross a governed operation boundary. */
export const GOVERNED_REFERENCE_KINDS = [
  'deal',
  'deal_commitment',
  'promise_set',
  'deal_thesis_outcome_version',
  'deal_thesis_objective_link',
  'strategy_objective_version',
  'outcome_measurement_plan',
  'governed_finding',
  'policy',
  'assessment',
  'workforce_reduction_run',
  'org_state',
] as const;
export type GovernedReferenceKind = (typeof GOVERNED_REFERENCE_KINDS)[number];

export function isGovernedReferenceKind(value: unknown): value is GovernedReferenceKind {
  return typeof value === 'string' && GOVERNED_REFERENCE_KINDS.some((kind) => kind === value);
}

/**
 * #5841 BAND-AUTHORITY P0 — an opaque, capability-owned band identity.
 *
 * A band is a dimension of a producer's governed figure (e.g. the M&A
 * module's promise bands). The vocabulary is owned by the capability pair
 * (producer stamps it, consumer pins it), NEVER by the generic manager: to
 * the projector and the structured-input builder this is a closed identity
 * token they may validate for equality and transport, but never interpret,
 * default, or derive from question text, labels, aliases, or intent ids.
 *
 * `bandKind` namespaces the vocabulary (one producer may govern several band
 * dimensions); `bandId` names the band inside it. Neither is a free-text
 * label — both are authored in module code next to the metric keys they
 * dimension.
 */
export type GovernedBandRef = Readonly<{
  bandKind: string;
  bandId: string;
}>;

export function isGovernedBandRef(value: unknown): value is GovernedBandRef {
  return (
    isUnknownRecord(value) &&
    isNonEmptyString(value.bandKind) &&
    isNonEmptyString(value.bandId)
  );
}

/** Identity equality, the ONLY operation the manager may perform on a band. */
export function governedBandRefEquals(
  left: GovernedBandRef | null | undefined,
  right: GovernedBandRef | null | undefined,
): boolean {
  if (left === null || left === undefined || right === null || right === undefined) return false;
  return left.bandKind === right.bandKind && left.bandId === right.bandId;
}

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isNonEmptyStringArray(value: unknown): value is readonly [string, ...string[]] {
  return isStringArray(value) && value.length > 0 && value.every(isNonEmptyString);
}

function isGovernedCurrencyRef(value: unknown): value is GovernedCurrencyRef {
  return (
    isUnknownRecord(value) && value.refKind === 'governed_currency' && typeof value.id === 'string'
  );
}

function isGovernedPeriodRef(value: unknown): value is GovernedPeriodRef {
  return (
    isUnknownRecord(value) && value.refKind === 'governed_period' && typeof value.id === 'string'
  );
}

function isGovernedScopeRef(value: unknown): value is GovernedScopeRef {
  return (
    isUnknownRecord(value) &&
    typeof value.scopeKind === 'string' &&
    typeof value.scopeId === 'string' &&
    (value.label === undefined || typeof value.label === 'string')
  );
}

function hasNumericIdentity(value: Record<string, unknown>): boolean {
  // Property READS (not `in`): they see inherited values through the prototype chain
  // while treating an explicit `key: undefined` as absent — matching the `?: never`
  // type surface, so every well-typed object also passes the runtime guard.
  return ['bindingKey', 'metricKey', 'unit', 'currencyRef', 'periodRef', 'scopeRefs'].some(
    (field) => value[field] !== undefined
  );
}

/**
 * A producer's own declared export of one governed binding. It carries no
 * second numeric value — `bindingKey` is the ONLY place the projector reads
 * the figure from, via the envelope's own `numericBindings[]`. See the
 * projector laws in `governed-argument-export-projector.ts` for what
 * "survive numeric publication fencing" requires before this may be trusted.
 */
export type GovernedNumericArgumentExportV1 = Readonly<{
  schemaVersion: 'governed_argument_export_v1';

  /** The reference-only discriminator must be absent from legacy numeric exports. */
  exportKind?: never;

  exportId: string;
  /** The `numericBindings[].placeholder` this export points to. */
  bindingKey: string;
  metricKey: string;

  valueBasis: GovernedValueBasis;

  unit: string;
  currencyRef: GovernedCurrencyRef | null;
  periodRef: GovernedPeriodRef | null;
  scopeRefs: readonly GovernedScopeRef[];

  evidenceBasis: GovernedEvidenceBasis;
  governanceStatus: GovernedGovernanceStatus;

  sourceRefIds: readonly string[];
  sourceValueHash: string;

  /**
   * #5841 BAND-AUTHORITY P0 — the producer's own stamped band identity when
   * this figure is one band of a banded comparison. Absent for a figure that
   * is flat across bands (stamping one would be a fabricated identity). The
   * consumer's `CapabilityTrustedReferenceContractV1.bandRef` pins the band
   * it binds; the projector validates equality and carries this onto the
   * minted reference verbatim.
   */
  bandRef?: GovernedBandRef;
}>;

/**
 * A producer's immutable governed object reference. Unlike a numeric export,
 * this carries no metric or binding identity: the shared projector can only
 * mint a trusted reference from the object lineage declared here. The dedicated
 * `exportKind: 'reference'` discriminator keeps this distinct from a legacy
 * numeric export whose `valueBasis` is also `'reference'`.
 */
export type GovernedParentVersion = Readonly<{
  referenceKind: 'promise_set';
  referenceId: string;
  version: string;
  contentDigest: string;
  dealRef: string;
}>;

export type GovernedReferenceExportDeclaration = Readonly<{ exportKey: string; referenceKind: GovernedReferenceKind }>;
export function isGovernedReferenceExportDeclaration(value: unknown): value is GovernedReferenceExportDeclaration {
  return isUnknownRecord(value) && isNonEmptyString(value.exportKey) && isGovernedReferenceKind(value.referenceKind);
}

export type GovernedReferenceArgumentExportV1 = Readonly<{
  schemaVersion: 'governed_argument_export_v1';

  exportId: string;
  exportKind: 'reference';
  /** Canonical manifest export group; member identity remains referenceId. */
  exportKey?: string;

  bindingKey?: never;
  metricKey?: never;
  unit?: never;
  currencyRef?: never;
  periodRef?: never;
  scopeRefs?: never;

  valueBasis: 'reference';
  referenceId: string;
  version: string;
  effectiveAt: string;
  tenantRef: string;

  producerIntentId: string;
  producerToolName: string;
  producerCallId: string;
  producerRunId?: string;

  /** Null when the referenced object has no applicable content digest. */
  contentDigest: string | null;
  sourceRefIds: readonly [string, ...string[]];
  evidenceBasis: GovernedEvidenceBasis;
  governanceStatus: GovernedGovernanceStatus;
  sourceValueHash: string;
}> & (
  | Readonly<{ referenceKind: 'deal_commitment'; parentVersion: GovernedParentVersion; producerRunId: string }>
  | Readonly<{ referenceKind: Exclude<GovernedReferenceKind, 'deal_commitment'>; parentVersion?: never }>
);

/** Embedded commitments inherit their parent revision/digest; they have no standalone version. */
export type GovernedReferenceAuthority = Readonly<{
  export: GovernedReferenceArgumentExportV1;
  producerRunId: string;
}>;

export function isGovernedReferenceAuthority(value: unknown): value is GovernedReferenceAuthority {
  return isUnknownRecord(value) && isNonEmptyString(value.producerRunId)
    && isGovernedReferenceArgumentExportV1(value.export) && value.export.producerRunId === value.producerRunId;
}

function parentVersionMatches(candidate: Record<string, unknown>): boolean {
  if (candidate.referenceKind !== 'deal_commitment') return candidate.parentVersion === undefined;
  const parent = candidate.parentVersion;
  return isUnknownRecord(parent) && parent.referenceKind === 'promise_set'
    && isNonEmptyString(candidate.producerRunId)
    && isNonEmptyString(parent.referenceId) && isNonEmptyString(parent.dealRef)
    && isNonEmptyString(parent.version) && isNonEmptyString(parent.contentDigest)
    && parent.version === candidate.version && parent.contentDigest === candidate.contentDigest;
}

/**
 * Backward-compat: this name predates the reference-valued amendment and every
 * existing consumer (e.g. the shared projector) reads it as the NUMERIC shape
 * with its fields required exactly as declared. It therefore stays an alias of
 * the numeric arm; new code that handles both arms uses the union name below.
 */
export type GovernedArgumentExportV1 = GovernedNumericArgumentExportV1;

/** Numeric and reference-valued exports share one projector/state path. */
export type GovernedArgumentExportUnionV1 =
  GovernedNumericArgumentExportV1 | GovernedReferenceArgumentExportV1;

export function isGovernedNumericArgumentExportV1(
  value: unknown
): value is GovernedNumericArgumentExportV1 {
  if (!isUnknownRecord(value) || value.exportKind !== undefined) return false;
  const candidate = value;
  return (
    candidate.schemaVersion === 'governed_argument_export_v1' &&
    typeof candidate.exportId === 'string' &&
    typeof candidate.bindingKey === 'string' &&
    typeof candidate.metricKey === 'string' &&
    isGovernedValueBasis(candidate.valueBasis) &&
    typeof candidate.unit === 'string' &&
    (candidate.currencyRef === null || isGovernedCurrencyRef(candidate.currencyRef)) &&
    (candidate.periodRef === null || isGovernedPeriodRef(candidate.periodRef)) &&
    Array.isArray(candidate.scopeRefs) &&
    candidate.scopeRefs.every(isGovernedScopeRef) &&
    isGovernedEvidenceBasis(candidate.evidenceBasis) &&
    isGovernedGovernanceStatus(candidate.governanceStatus) &&
    isStringArray(candidate.sourceRefIds) &&
    typeof candidate.sourceValueHash === 'string' &&
    (candidate.bandRef === undefined || isGovernedBandRef(candidate.bandRef))
  );
}

export function isGovernedReferenceArgumentExportV1(
  value: unknown
): value is GovernedReferenceArgumentExportV1 {
  if (!isUnknownRecord(value) || hasNumericIdentity(value)) return false;
  const candidate = value;
  return (
    candidate.schemaVersion === 'governed_argument_export_v1' &&
    isNonEmptyString(candidate.exportId) &&
    candidate.exportKind === 'reference' &&
    (candidate.exportKey === undefined || isNonEmptyString(candidate.exportKey)) &&
    candidate.valueBasis === 'reference' &&
    isGovernedReferenceKind(candidate.referenceKind) &&
    parentVersionMatches(candidate) &&
    isNonEmptyString(candidate.referenceId) &&
    isNonEmptyString(candidate.version) &&
    isNonEmptyString(candidate.effectiveAt) &&
    isNonEmptyString(candidate.tenantRef) &&
    isNonEmptyString(candidate.producerIntentId) &&
    isNonEmptyString(candidate.producerToolName) &&
    isNonEmptyString(candidate.producerCallId) &&
    (candidate.contentDigest === null || isNonEmptyString(candidate.contentDigest)) &&
    isNonEmptyStringArray(candidate.sourceRefIds) &&
    isGovernedEvidenceBasis(candidate.evidenceBasis) &&
    isGovernedGovernanceStatus(candidate.governanceStatus) &&
    isNonEmptyString(candidate.sourceValueHash)
  );
}

/** Backward-compat: numeric-only, exactly the pre-amendment semantics. */
export function isGovernedArgumentExportV1(value: unknown): value is GovernedArgumentExportV1 {
  return isGovernedNumericArgumentExportV1(value);
}

export function isGovernedArgumentExportUnionV1(
  value: unknown
): value is GovernedArgumentExportUnionV1 {
  return isGovernedNumericArgumentExportV1(value) || isGovernedReferenceArgumentExportV1(value);
}

/**
 * A consumer's exact acceptance contract for a server-minted reference
 * export. It is intentionally distinct from the numeric trusted-reference
 * contract below: reference identity cannot be expressed as a metric alias.
 */
export type ConsumerReferenceRequirement = Readonly<{
  consumerIntentId: string;
  requirementId: string;
  acceptedReferenceKinds: readonly [GovernedReferenceKind, ...GovernedReferenceKind[]];
  requireSameTenant: true;
  sourceIntentIds: readonly [string, ...string[]];
}>;

export function isConsumerReferenceRequirement(
  value: unknown
): value is ConsumerReferenceRequirement {
  if (!isUnknownRecord(value)) return false;
  return (
    isNonEmptyString(value.consumerIntentId) &&
    isNonEmptyString(value.requirementId) &&
    Array.isArray(value.acceptedReferenceKinds) &&
    value.acceptedReferenceKinds.length > 0 &&
    value.acceptedReferenceKinds.every(isGovernedReferenceKind) &&
    value.requireSameTenant === true &&
    isNonEmptyStringArray(value.sourceIntentIds)
  );
}

/** How strictly a consumer requirement demands scope compatibility. */
export const CAPABILITY_TRUSTED_REFERENCE_SCOPE_COMPATIBILITIES = [
  'same_scope',
  'declared_compatible',
] as const;
export type CapabilityTrustedReferenceScopeCompatibility =
  (typeof CAPABILITY_TRUSTED_REFERENCE_SCOPE_COMPATIBILITIES)[number];

/**
 * A consumer's declared willingness to accept a server-minted trusted
 * reference for one required input, instead of (or in addition to) a
 * reader-cited value. Optional on `CapabilityRequirement` — a requirement
 * that omits this never admits a cross-operation reference, only a request
 * citation.
 */
export type CapabilityTrustedReferenceContractV1 = Readonly<{
  acceptedMetricKeys: readonly string[];
  acceptedValueBases: readonly GovernedValueBasis[];
  acceptedEvidenceBases: readonly GovernedEvidenceBasis[];

  requireSameTenant: true;
  requireSameCurrency: boolean;
  requireSamePeriod: boolean;
  requiredScopeCompatibility: CapabilityTrustedReferenceScopeCompatibility;

  /** When present, only an export produced by one of these intents may satisfy this requirement. */
  sourceIntentIds?: readonly string[];

  /**
   * #5841 BAND-AUTHORITY P0 — when present, this requirement binds exactly
   * ONE band identity: only an export whose own `bandRef` is equal admits,
   * and an otherwise-matching export carrying no band identity names the
   * typed `export_missing_band_identity` gap (never a silent mint, never a
   * silent drop). Absent = the requirement is not band-dimensioned. The
   * capability manifest owns this declaration; the manager validates and
   * transports it, and may never default it.
   */
  bandRef?: GovernedBandRef;
}>;

export function isCapabilityTrustedReferenceContractV1(
  value: unknown
): value is CapabilityTrustedReferenceContractV1 {
  if (!isUnknownRecord(value)) return false;
  const candidate = value;
  return (
    Array.isArray(candidate.acceptedMetricKeys) &&
    Array.isArray(candidate.acceptedValueBases) &&
    Array.isArray(candidate.acceptedEvidenceBases) &&
    candidate.requireSameTenant === true &&
    typeof candidate.requireSameCurrency === 'boolean' &&
    typeof candidate.requireSamePeriod === 'boolean' &&
    (candidate.requiredScopeCompatibility === 'same_scope' ||
      candidate.requiredScopeCompatibility === 'declared_compatible') &&
    (candidate.bandRef === undefined || isGovernedBandRef(candidate.bandRef))
  );
}

/**
 * #5841 BAND-AUTHORITY P0 — the capability-owned declaration that lets the
 * GENERIC manager build a consumer's structured tool input without knowing
 * anything about the capability: no intent-id branch, no requirement-id
 * selection, no band vocabulary in central-manager code.
 *
 * The declaration maps the intent's own declared `requiredInputs` onto the
 * tool-input fields its tool actually consumes. Every value transported
 * through it is already governed: identity fields carry the plain string
 * identity minted for that requirement; operand fields carry the minted
 * export-backed reference's own provenance fields verbatim. A `bandField`
 * receives the minted reference's VALIDATED band id (`GovernedBandRef.bandId`
 * — proven equal to the requirement's `trustedReferenceContract.bandRef` at
 * mint time by the projector). The band VALUE is never declared here: the
 * relation between bands and requirements lives in the contracts, and the
 * identities arrive on the references.
 *
 * An intent that omits this declaration keeps the plain scalar `arguments`
 * path, byte-identical to before this contract existed.
 */
export const CAPABILITY_STRUCTURED_TOOL_INPUT_SCHEMA_VERSION =
  'capability_structured_tool_input_v1';

export type CapabilityStructuredIdentityFieldV1 = Readonly<{
  /** A declared `requiredInputs[].requirementId` of the owning intent. */
  requirementId: string;
  /** The tool-input field receiving the requirement's plain string identity value. */
  field: string;
}>;

export type CapabilityStructuredOperandFieldV1 = Readonly<{
  /** A declared `requiredInputs[].requirementId` of the owning intent, backed by a trusted-reference contract. */
  requirementId: string;
  /** The tool-input field receiving the projected operand object. */
  field: string;
  /**
   * Set when this operand is one band of a banded comparison: the tool-input
   * field that receives the minted reference's validated band id. Requires
   * the requirement's contract to pin `bandRef`; a missing or mismatched
   * band identity fails the build (a typed gap upstream), never a default.
   */
  bandField?: string;
}>;

export type CapabilityStructuredToolInputV1 = Readonly<{
  schemaVersion: typeof CAPABILITY_STRUCTURED_TOOL_INPUT_SCHEMA_VERSION;
  identityFields: readonly CapabilityStructuredIdentityFieldV1[];
  operandFields: readonly CapabilityStructuredOperandFieldV1[];
}>;

function isStructuredField(value: unknown): value is CapabilityStructuredIdentityFieldV1 {
  return isUnknownRecord(value) && isNonEmptyString(value.requirementId) && isNonEmptyString(value.field);
}

export function isCapabilityStructuredToolInputV1(
  value: unknown,
): value is CapabilityStructuredToolInputV1 {
  if (!isUnknownRecord(value)) return false;
  return (
    value.schemaVersion === CAPABILITY_STRUCTURED_TOOL_INPUT_SCHEMA_VERSION &&
    Array.isArray(value.identityFields) &&
    value.identityFields.every(isStructuredField) &&
    Array.isArray(value.operandFields) &&
    value.operandFields.every(
      (operand) =>
        isStructuredField(operand) &&
        ((operand as CapabilityStructuredOperandFieldV1).bandField === undefined ||
          isNonEmptyString((operand as CapabilityStructuredOperandFieldV1).bandField)),
    )
  );
}
